'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { fetchCategoryTagProducts, Product } from '@/lib/fetchPitanieCategoryTagProducts';
import { Pagination } from './Pagination';
import AddToCartButtonInList from '@/components/add_to_cart_popup/AddToCartButtonInList';
import { MiniCartProvider } from '@/app/context/MiniCartContext';

const parsePrice = (price: string): number => {
    const cleanedPrice = price.replace(/[^\d.]/g, '');
    const num = parseFloat(cleanedPrice);
    return isNaN(num) ? 0 : num;
};

interface CategoryTagProductsClientProps {
    initialProducts: Product[];
    initialTotal: number;
    initialTotalPages: number;
    currentPage: number;
    perPage: number;
    categorySlug: string;
    tagSlug: string;
}

export function CategoryTagProductsClient({
    initialProducts,
    initialTotal,
    initialTotalPages,
    currentPage,
    perPage,
    categorySlug,
    tagSlug,
}: CategoryTagProductsClientProps) {
    const [products, setProducts] = useState(initialProducts);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [currentLoadedPage, setCurrentLoadedPage] = useState(currentPage);
    const searchParams = useSearchParams();
    const router = useRouter();

    const [isLoadMore, setIsLoadMore] = useState(false);

    useEffect(() => {
        if (!isLoadMore) {
            setProducts(initialProducts);
            setCurrentLoadedPage(currentPage);
        }
        setIsLoadMore(false);
    }, [searchParams.get('page'), initialProducts, currentPage]);

    const loadMore = async () => {
        if (isLoadingMore) return;
        setIsLoadingMore(true);
        setIsLoadMore(true);

        try {
            const nextPage = currentLoadedPage + 1;
            const { products: newProducts } = await fetchCategoryTagProducts(categorySlug, tagSlug, nextPage, perPage);
            setProducts((prevProducts) => [...prevProducts, ...newProducts]);
            setCurrentLoadedPage(nextPage);
            router.push(`/produkty-pitanie?page=${nextPage}`, { scroll: false });
        } catch (error) {
            console.error('Error loading more category products:', error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    return (
        <div className="products_side">
            <p className="mb-[15px] brandsCount" style={{ marginTop: '10px' }}>
                Товаров: <strong>{initialTotal}</strong>
            </p>

            <div className="shop_page_prod_grid">
                {products.map((p) => {
                    const priceNumber = parsePrice(p.price || '0');
                    const formattedPrice = priceNumber.toLocaleString('ru-RU') + ' сӯм';
                    const isInStock = p.stock_status === 'instock' || (p.stock_quantity && p.stock_quantity > 0);
                    const productClasses = !isInStock ? 'product_item grayscale opacity-80' : 'product_item';

                    return (
                        <div className={productClasses} key={p.id}>
                            <Link href={`/product/${p.slug}`}>
                                <Image
                                    className="product_item__image"
                                    src={p.images[0]?.src || '/images/products/default.jpg'}
                                    alt={p.images[0]?.alt || p.name}
                                    width={200}
                                    height={200}
                                />
                            </Link>

                            <div className="product_meta_box">
                                <div className="product_item__brand_sku flex items-center justify-between">
                                    {p.brands?.length > 0 ? (
                                        <Link
                                            href={`/product-brands/${p.brands[0].slug || ''}`}
                                            className="product_item__brand"
                                        >
                                            {p.brands[0].name || 'Бренд не указан'}
                                        </Link>
                                    ) : (
                                        <span className="product_item__brand">Бренд не указан</span>
                                    )}
                                    {p.sku && (
                                        <span style={{ margin: '0px !important' }} className="product_item__sku stock_count m-0">{p.sku}</span>
                                    )}
                                </div>
                                <div className="line_highlight"></div>
                                <Link href={`/product/${p.slug}`} className="product_item__name">
                                    {p.name}
                                </Link>
                                <span className="product_item__price">{formattedPrice}</span>
                            </div>

                            {/* <MiniCartProvider>
                                <AddToCartButtonInList
                                    productId={p.id.toString()}
                                    productName={p.name}
                                    productImage={p.images[0]?.src ?? '/images/products/default.jpg'}
                                    productPrice={priceNumber}
                                    maxQuantity={p.stock_quantity || 0}
                                />
                            </MiniCartProvider> */}

                            <MiniCartProvider>
                                <AddToCartButtonInList
                                    productId={btoa(`post:${p.id}`)} // Формируем строку post:<p.id> и кодируем в base64
                                    productName={p.name}
                                    productImage={p.images[0]?.src ?? '/images/products/default.jpg'}
                                    productPrice={priceNumber}
                                    maxQuantity={p.stock_quantity || 0}
                                />
                            </MiniCartProvider>
                        </div>
                    );
                })}
            </div>

            <div className="shop_actions">
                {currentLoadedPage < initialTotalPages && (
                    <div className="load_more_container">
                        <button
                            onClick={loadMore}
                            disabled={isLoadingMore}
                            className="load_more_button text-black bg-[#ebebeb] hover:bg-[#ebebeb]"
                        >
                            {isLoadingMore ? 'Загрузка...' : 'Загрузить еще'}
                        </button>
                    </div>
                )}

                <div className="pagination_controls">
                    {initialTotalPages > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={initialTotalPages}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}